import { HOME_CODE } from "@rakazo/contracts";
import { useEffect, useRef } from "react";
import { createJellyfishLight } from "./jellyfish-light";

/** Sparse glyphs and a fading pointer trail, adapted from Zeron's MIT glyph-field.
 * Copyright (c) 2026 Wing. See third-party/zeron-glyph-field.LICENSE. */
export function CodeField({ swimming = false }: { swimming?: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    const base = document.createElement("canvas");
    const baseContext = base.getContext("2d")!;
    const host = canvas.parentElement!;
    const reduced = matchMedia("(prefers-reduced-motion: reduce)");
    const source = HOME_CODE.join("     ");
    const font = '11px ui-monospace, "SFMono-Regular", Menlo, monospace';
    const jellyfishLight = createJellyfishLight();
    let pointer: { x: number; y: number } | null = null;
    const trail: { x: number; y: number; born: number }[] = [];
    let width = 0,
      height = 0,
      cols = 0,
      frame = 0,
      glow = "";
    let visible = true,
      lastPaint = 0,
      previousTime = 0,
      swimTime = 0;
    const canSwim = () =>
      swimming &&
      !reduced.matches &&
      !document.hidden &&
      visible &&
      (!document.fullscreenElement || document.fullscreenElement.contains(canvas));
    let cells = new Map<number, { x: number; y: number; char: string }>();
    const paint = (now = performance.now()) => {
      frame = 0;
      // Navigation and window resizing can briefly detach or collapse the canvas.
      if (!canvas.isConnected || !width || !height || !base.width || !base.height) {
        frame = 0;
        return;
      }
      const swim = canSwim();
      if (swim && now - lastPaint < 1000 / 30) {
        frame = requestAnimationFrame(paint);
        return;
      }
      if (swim && previousTime) swimTime += Math.min((now - previousTime) / 1000, 0.1);
      previousTime = swim ? now : 0;
      lastPaint = now;
      while (trail.length && now - trail[0]!.born > 650) trail.shift();
      context.clearRect(0, 0, width, height);
      context.globalAlpha = 1;
      context.drawImage(base, 0, 0, width, height);
      context.font = font;
      context.fillStyle = glow;
      const heat = new Map<number, number>();
      const light = (x: number, y: number, radius: number, strength: number) => {
        for (
          let row = Math.max(0, Math.floor((y - radius) / 13));
          row <= Math.min(Math.ceil(height / 13), Math.ceil((y + radius) / 13));
          row++
        ) {
          for (
            let col = Math.max(0, Math.floor((x - radius) / 7));
            col <= Math.min(cols - 1, Math.ceil((x + radius) / 7));
            col++
          ) {
            const key = row * cols + col,
              cell = cells.get(key);
            if (!cell) continue;
            const dx = cell.x - x,
              dy = cell.y - y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const value = Math.max(0, 1 - distance / radius) ** 2 * strength;
            if (value > (heat.get(key) ?? 0)) heat.set(key, value);
          }
        }
      };
      if (swim) jellyfishLight(width, height, swimTime, light, pointer);
      for (const point of trail) {
        const life = 1 - (now - point.born) / 650;
        light(point.x, point.y, 10 + 48 * life, life ** 2 * 0.3);
      }
      for (const [key, value] of heat) {
        const cell = cells.get(key)!;
        context.globalAlpha = value;
        context.fillText(cell.char, cell.x, cell.y);
      }
      frame = swim || trail.length ? requestAnimationFrame(paint) : 0;
    };
    const resize = () => {
      const ratio = Math.min(devicePixelRatio || 1, 2);
      width = host.clientWidth;
      height = host.clientHeight;
      cols = Math.ceil(width / 7);
      canvas.width = base.width = width * ratio;
      canvas.height = base.height = height * ratio;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      baseContext.setTransform(ratio, 0, 0, ratio, 0, 0);
      const style = getComputedStyle(canvas);
      baseContext.fillStyle = style.getPropertyValue("--code-field").trim() || style.color;
      baseContext.font = font;
      glow = style.getPropertyValue("--code-glow").trim() || style.color;
      cells = new Map();
      for (let row = 0; row < height / 13; row++)
        for (let col = 0; col < cols; col++) {
          const seed = ((row * 928371 + col * 364479) >>> 0) % 100;
          const x = col * 7,
            y = row * 13;
          const char = source[(row * 41 + col) % source.length] ?? " ";
          cells.set(row * cols + col, { x, y, char });
          baseContext.globalAlpha = 0.06 + seed / 1800;
          if (seed <= 62) baseContext.fillText(char, x, y);
        }
      cancelAnimationFrame(frame);
      lastPaint = 0;
      paint();
    };
    const move = (event: PointerEvent) => {
      if (reduced.matches) return;
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left,
        y = event.clientY - rect.top;
      if (event.pointerType !== "touch") pointer = { x, y };
      trail.push({
        x,
        y,
        born: performance.now(),
      });
      if (trail.length > 30) trail.shift();
      if (!frame) frame = requestAnimationFrame(paint);
    };
    const leave = () => {
      pointer = null;
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    const sync = () => {
      cancelAnimationFrame(frame);
      lastPaint = previousTime = 0;
      trail.length = 0;
      pointer = null;
      paint();
    };
    const visibility = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? false;
      sync();
    });
    visibility.observe(canvas);
    document.addEventListener("visibilitychange", sync);
    document.addEventListener("fullscreenchange", sync);
    reduced.addEventListener("change", sync);
    host.addEventListener("pointermove", move, { passive: true });
    host.addEventListener("pointerleave", leave);
    window.addEventListener("blur", leave);
    const themeObserver = new MutationObserver(resize);
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme"],
    });
    resize();
    return () => {
      observer.disconnect();
      visibility.disconnect();
      document.removeEventListener("visibilitychange", sync);
      document.removeEventListener("fullscreenchange", sync);
      reduced.removeEventListener("change", sync);
      themeObserver.disconnect();
      cancelAnimationFrame(frame);
      host.removeEventListener("pointermove", move);
      host.removeEventListener("pointerleave", leave);
      window.removeEventListener("blur", leave);
    };
  }, [swimming]);
  return (
    <canvas
      ref={ref}
      aria-hidden
      className="pointer-events-none absolute inset-0 size-full text-muted-foreground"
    />
  );
}
